import { isEventExpiredForUi } from 'util/isEventExpiredForUi';
import { Event, GetProfileResponse, OrganizationSummary } from 'services/api';
import { RoleMap } from 'hooks/useGetRole';
import { EVENT_BRIEF_USER_STATUS_LABEL } from 'components/ui/EventStatus/eventBriefRequestStatus.constants';

export const LIVE_STATUSES: Event['status'][] = ['PUBLISHED', 'UPDATED', 'RESCHEDULED'];

/** Live, finished, cancelled, expired — approvers must not see Deny/Approve. */
export const APPROVER_ACTION_BLOCKED_STATUSES: Event['status'][] = [
  ...LIVE_STATUSES,
  'FINISHED',
  'CANCELLED',
  'EXPIRED',
];

export function isApproverActionBlockedByEventStatus (event: Event): boolean {
  if (APPROVER_ACTION_BLOCKED_STATUSES.includes(event.status)) return true;
  return isEventExpiredForUi(event);
}

export function getPrimaryHostOrganization (
  hostOrganizations: OrganizationSummary[] | undefined,
): OrganizationSummary | undefined {
  if (!hostOrganizations?.length) return undefined;
  return (
    hostOrganizations.find(o => o.hostRole === 'PRIMARY') ?? hostOrganizations[0]
  );
}

/** Same strings as API `userEventStatus`; single source: event brief status constants. */
export const UserEventStatus = EVENT_BRIEF_USER_STATUS_LABEL;

export const hasUserApproved = (
  event: Event,
  profileId: string | undefined,
  checkApproverLogs = true,
  checkAdminLogs = true,
): boolean => {
  if (checkApproverLogs && event.approverLogs && event.approverLogs.length > 0) {
    const approved = event.approverLogs.some(categoryLog =>
      categoryLog.approvers.some(
        approver => approver.approverId === profileId && approver.action === 'approved',
      ),
    );
    if (approved) return true;
  }
  if (checkAdminLogs && event.adminLogs && event.adminLogs.length > 0) {
    const approved = event.adminLogs.some(adminLog =>
      adminLog.approvers.some(
        approver => approver.approverId === profileId && approver.action === 'approved',
      ),
    );
    if (approved) return true;
  }
  return false;
};

export const shouldShowApprovalButtons = (
  event: Event,
  role: string[] | undefined,
  profile: { id?: string; isSequentialApprover?: boolean } | null | undefined,
): boolean => {
  if (!profile || profile?.isSequentialApprover) return false;
  if (!role?.includes(RoleMap.approver) && !role?.includes(RoleMap.admin)) return false;
  if (isApproverActionBlockedByEventStatus(event)) return false;
  if (
    event.userEventStatus === UserEventStatus.AWAITING_PREVIOUS_STEP ||
    event.userEventStatus === UserEventStatus.NO_ACTION_TAKEN
  )
    return false;
  const approved = hasUserApproved(event, profile?.id);
  const hasLogs =
    (event.approverLogs && event.approverLogs.length > 0) ||
    (event.adminLogs && event.adminLogs.length > 0);
  return !approved || !hasLogs;
};

export const shouldShowSequentialApprovalButtons = (
  event: Event,
  profile: GetProfileResponse | undefined,
): boolean => {
  if (!event.isSequentialApprovalEnabled || !profile || !profile?.isSequentialApprover)
    return false;
  if (isApproverActionBlockedByEventStatus(event)) return false;
  if (
    event.userEventStatus === UserEventStatus.AWAITING_PREVIOUS_STEP ||
    event.userEventStatus === UserEventStatus.NO_ACTION_TAKEN
  )
    return false;
  const approvalSequence = event.sequenceApprovalLogs?.find(log =>
    log.approvers.some(approver => approver.approverId === profile?.id),
  );
  const approverLog = approvalSequence?.approvers.find(
    approver => approver.approverId === profile?.id,
  );
  return approverLog?.action !== 'approved';
};
